
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { TimeItem } from "../types";
import { toast } from "@/components/ui/use-toast";
import { v4 as uuidv4 } from "uuid";

export const useTimesDatabase = (userId?: string) => {
  const [isLoading, setIsLoading] = useState(false);
  
  const fetchTimesFromDatabase = async () => {
    if (!userId) {
      toast({
        variant: "destructive",
        title: "שגיאה בטעינת הזמנים",
        description: "יש להתחבר כדי לצפות ולערוך זמנים"
      });
      return null;
    }

    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('times')
        .select('*')
        .order('created_at', { ascending: true });
      
      if (error) {
        console.error("Error fetching times:", error);
        toast({
          variant: "destructive",
          title: "שגיאה בטעינת הזמנים",
          description: error.message
        });
        return null;
      }
      
      if (data && data.length > 0) {
        return data.map(item => ({
          id: item.id,
          title: item.title,
          time: item.time,
          prayer_type: item.prayer_type,
          is_relative: item.is_relative,
          relative_to: item.relative_to,
          offset_minutes: item.offset_minutes,
          show_time: item.show_time
        }));
      }
      return [];
    } catch (error: any) {
      console.error("Error in fetchTimesFromDatabase:", error);
      toast({
        variant: "destructive",
        title: "שגיאה בטעינת הזמנים",
        description: "אירעה שגיאה בטעינת הזמנים מהשרת"
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const addTimeToDatabase = async (newTime: TimeItem) => {
    if (!userId) {
      toast({
        variant: "destructive",
        title: "שגיאה בהוספת זמן",
        description: "יש להתחבר כדי להוסיף זמנים"
      });
      return null;
    }

    try {
      // Add to database
      const { error } = await supabase
        .from('times')
        .insert({
          id: newTime.id,
          user_id: userId,
          title: newTime.title,
          time: newTime.time,
          prayer_type: newTime.prayer_type,
          is_relative: newTime.is_relative,
          relative_to: newTime.relative_to,
          offset_minutes: newTime.offset_minutes,
          show_time: newTime.show_time
        });

      if (error) {
        console.error("Error adding time:", error);
        toast({
          variant: "destructive",
          title: "שגיאה בהוספת זמן",
          description: error.message
        });
        return null;
      }
      
      toast({
        title: "נוסף זמן חדש",
        description: "הזמן החדש נוסף בהצלחה"
      });
      
      return newTime;
    } catch (error) {
      console.error("Error in addTimeToDatabase:", error);
      toast({
        variant: "destructive",
        title: "שגיאה בהוספת זמן",
        description: "אירעה שגיאה בהוספת הזמן החדש"
      });
      return null;
    }
  };

  const updateTimeInDatabase = async (id: string, field: string, value: string | boolean | number) => {
    if (!userId) {
      toast({
        variant: "destructive",
        title: "שגיאה בעדכון הזמן",
        description: "יש להתחבר כדי לעדכן זמנים"
      });
      return false;
    }

    try {
      const { error } = await supabase
        .from('times')
        .update({ [field]: value })
        .eq('id', id);
        
      if (error) {
        console.error(`Error updating time ${field}:`, error);
        toast({
          variant: "destructive",
          title: "שגיאה בעדכון הזמן",
          description: error.message
        });
        return false;
      }
      return true;
    } catch (error) {
      console.error("Error in updateTimeInDatabase:", error);
      toast({
        variant: "destructive",
        title: "שגיאה בעדכון הזמן",
        description: "אירעה שגיאה בעדכון הזמן"
      });
      return false;
    }
  };

  const deleteTimeFromDatabase = async (id: string) => {
    if (!userId) {
      toast({
        variant: "destructive",
        title: "שגיאה במחיקת הזמן",
        description: "יש להתחבר כדי למחוק זמנים"
      });
      return false;
    }

    try {
      const { error } = await supabase
        .from('times')
        .delete()
        .eq('id', id);
        
      if (error) {
        console.error("Error deleting time:", error);
        toast({
          variant: "destructive",
          title: "שגיאה במחיקת הזמן",
          description: error.message
        });
        return false;
      }
      
      toast({
        title: "הזמן נמחק",
        description: "הזמן נמחק בהצלחה"
      });
      
      return true;
    } catch (error) {
      console.error("Error in deleteTimeFromDatabase:", error);
      toast({
        variant: "destructive",
        title: "שגיאה במחיקת הזמן",
        description: "אירעה שגיאה במחיקת הזמן"
      });
      return false;
    }
  };
  
  const createNewTime = () => {
    return {
      id: uuidv4(),
      title: "זמן חדש",
      time: "00:00",
      is_relative: false,
      prayer_type: "", // Setting empty string for backward compatibility
      offset_minutes: 0,
      show_time: true
    };
  };

  return {
    isLoading,
    fetchTimesFromDatabase,
    addTimeToDatabase,
    updateTimeInDatabase,
    deleteTimeFromDatabase,
    createNewTime
  };
};
